import React from 'react';
import { Users, Search, Bookmark } from 'lucide-react';
import { ACTIVITY_LABEL, ActivityType, DEFAULT_ACTIVITY_COLORS } from '../lib/activityTypes';
import { DATA_SOURCE_LABEL } from '../lib/dataSources';
import type { Activity } from './feed';

interface ActivityCardProps {
  activity: Activity;
  isSaved: boolean;
  onSave: () => void;
  onShare: () => void;
  onSearch: () => void;
}

const getActivityTag = (type: ActivityType) => ({
  text: ACTIVITY_LABEL[type] || 'Activity',
  color: DEFAULT_ACTIVITY_COLORS[type]
});

const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((+now - +date) / 60000);
  if (diffInMinutes < 1) return 'now';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  return `${Math.floor(diffInMinutes / 1440)}d`;
};

const ActivityCard: React.FC<ActivityCardProps> = ({ activity, isSaved, onSave, onShare, onSearch }) => {
  const activityTag = getActivityTag(activity.type);
  const companyName = activity.company || activity.well?.company;
  const wellName = activity.wellName || activity.well?.name;
  const location = activity.locationName || activity.location;
  const metaInfo = [companyName, wellName, location].filter(Boolean).join(' | ');

  return (
    <article className="group p-4 hover:bg-gray-950/50 transition-colors cursor-pointer">
      <header className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-white"
          style={{ backgroundColor: activityTag.color }}
        >
          {activityTag.text}
        </span>
        <div className="flex flex-col">
          {activity.activity && (
            <h3 className="text-white font-bold text-sm sm:text-base">{activity.activity}</h3>
          )}
          {metaInfo && <div className="text-sm text-gray-400">{metaInfo}</div>}
        </div>
      </header>

      {activity.description && (
        <p className="text-white mb-2 whitespace-pre-wrap">{activity.description}</p>
      )}

      {activity.photo && (
        <figure className="mb-2 overflow-hidden rounded-lg border border-gray-700">
          {(() => {
            const url = activity.photo.replace('?size=medium', '');
            return (
              <img
                src={`${url}?size=medium`}
                srcSet={`${url}?size=thumb 400w, ${url}?size=medium 800w, ${url} 1920w`}
                sizes="(max-width: 768px) 100vw, 768px"
                loading="eager"
                fetchPriority="high"
                width={768}
                height={432}
                className="w-full h-auto"
                alt="Field report"
                onError={e => {
                  const img = e.currentTarget;
                  if (img.src.includes('?size=thumb') || img.src.includes('?size=medium')) {
                    img.onerror = null;
                    img.src = url;
                  }
                }}
              />
            );
          })()}
        </figure>
      )}

      <div className="flex items-center gap-4 mt-4">
        <button
          onClick={onSave}
          aria-label={isSaved ? 'Unsave activity' : 'Save activity'}
          title={isSaved ? 'Unsave' : 'Save'}
          className={`flex items-center gap-2 rounded-lg px-3 py-2 transition-colors text-sm font-medium ${
            isSaved ? 'text-white bg-gray-800' : 'text-gray-500 hover:text-white hover:bg-gray-800'
          }`}
        >
          <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
          <span>{isSaved ? 'Unsave' : 'Save'}</span>
        </button>

        <button
          onClick={onShare}
          aria-label="Share activity"
          title="Share"
          className="flex items-center gap-2 text-gray-500 hover:text-green-400 hover:bg-green-400/10 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
        >
          <Users className="w-4 h-4" />
          <span>Share</span>
        </button>

        <button
          onClick={onSearch}
          aria-label="Find similar activities"
          title="More like this"
          className="flex items-center gap-2 text-gray-500 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg px-3 py-2 transition-colors text-sm font-medium"
        >
          <Search className="w-4 h-4" />
          <span>More like this</span>
        </button>
      </div>

      <div className="mt-4 text-gray-500 text-sm">
        {activity.source && (
          <>
            <span>Source: {DATA_SOURCE_LABEL[activity.source]}</span>
            <span> · </span>
          </>
        )}
        <span>{formatTimeAgo(activity.createdAt)} ago</span>
      </div>
    </article>
  );
};

export default ActivityCard;

